<template>
  <v-card class="mb-4" min-width="360px" flat>
    <!-- CHART -->
    <div :id="'chart-' + chartName" class="doughnut-chart noselect">
      <canvas :id="chartName" height="100%" width="100%"></canvas>
    </div>

    <!-- TITLE OF Chart -->
    <v-row no-gutters>
      <v-col cols="8">
        <div class="text-h5 text-center text-uppercase mt-1">{{ value.legendTitle }}</div>
      </v-col>
      <v-col cols="4">
        <div class="text-subtitle-1 text-center mt-2">{{ value.legendCount }}</div>
      </v-col>
    </v-row>
      <v-divider></v-divider>

    <!-- LEGEND -->
    <div :id="'chartjs-legend-' + chartName" class="chartjs-legend noselect" v-html="legendMarkup">
    </div>

  </v-card>
</template>

<script>
import 'chartjs-plugin-colorschemes'
import Chart from 'chart.js'
import { mapState } from 'vuex'
import { path } from 'ramda'
import { CHART_COLORS } from '@/components/charts'

const { v4: _uuid } = require('uuid')

export default {
  name: 'circle-chart',
  props: {
    value: {
      type: Object,
      required: false
    }
  },
  data: function () {
    return {
      ui: {
        chartMaxHeight: 'max-height: 500px'
      },
      chartName: null,
      chart: null,
      legendMarkup: ''
    }
  },
  computed: {
    ...mapState('DealerStore', ['dealers']),
    ...mapState({
      accountKey: state =>
        path(['attributes', 'custom:account_key'], state.cognito.user)
    }),
    chartColors () {
      if (this.value.chartLabels.includes('noop')) {
        const noopIdx = this.value.chartLabels.indexOf('noop')
        return Object.assign([], CHART_COLORS, { [noopIdx]: '#D3D3D3' })
      } else {
        return CHART_COLORS
      }
    },
    chartData () {
      return {
        type: 'doughnut',
        data: {
          labels: this.value.chartLabels,
          datasets: [{
            data: this.value.chartData,
            backgroundColor: this.chartColors,
            hoverBackgroundColor: this.chartColors
          }]
        },
        options: {
          doughnutCircleText: {
            heading: `${this.value.chartHeading}%`,
            title: this.value.chartTitle,
            subtitle: this.value.chartSubtitle
          },
          responsive: true,
          maintainAspectRatio: false,
          cutoutPercentage: 60,
          legend: {
            display: false
          },
          legendCallback: function (chart) {
            const legendItem = (label, color, currentValue, allData) => {
              if (label === 'noop') {
                return ''
              }
              const pct = this.value.labelPercentages.find(lpData => lpData.label === label).value

              return `
                <div class="row no-gutters">
                  <div class="col col-6 offset-1">
                    <div class="square" style="border-color:${color}; background-color:${color}"><img/> </div>
                      <span class="ml-1">${label}</span>
                  </div>
                  <div class="col col-2 text-right">
                      <span class="font-weight-bold mr-2">${currentValue}</span>
                  </div>
                  <div class="col col-2 text-right">
                      ${pct}%
                  </div>
                  <div class="col col-1">
                     &nbsp;
                  </div>
                </div>
                `
            }
            var text = []
            for (var i = 0; i < chart.data.datasets[0].data.length; i++) {
              text.push(legendItem(chart.data.labels[i], chart.data.datasets[0].backgroundColor[i], chart.data.datasets[0].data[i], chart.data.datasets[0].data))
            }

            return `<div class="container">${text.join('')}</div>`
          }.bind(this),
          tooltips: {
            callbacks: {
              label: function (tooltipItem, data) {
                if (this.value.labelPercentages[tooltipItem.index].label === 'noop') {
                  return 'n/a'
                }
                var dataset = data.datasets[tooltipItem.datasetIndex]
                var currentValue = dataset.data[tooltipItem.index]
                var percentage = this.value.labelPercentages[tooltipItem.index].value

                return currentValue + ' (' + percentage + '%)'
              }.bind(this),
              title: function (tooltipItem, data) {
                const lbl = data.labels[tooltipItem[0].index]
                if (lbl === 'noop') {
                  return 'EMPTY'
                } else {
                  return lbl.toUpperCase()
                }
              }
            }
          },
          plugins: {
            doughnutCircleText: true,
            datalabels: {
              display: false // this was the trick to turning off point labels - have to override plugin
            }
          }
        }
      }
    }
  },
  watch: {
    value: {
      immediate: false,
      handler (newVal, oldVal) {
        this.refreshChart()
      }
    }
  },
  methods: {
    findOrCreateChart (chartId, chartData) {
      if (this.chart) {
        this.chart.type = chartData.type
        this.chart.data = chartData.data
        this.chart.options = chartData.options
        this.chart.update()
        return this.chart
      } else {
        const ctx = document.getElementById(chartId)
        const chart = new Chart(ctx, {
          type: chartData.type,
          data: chartData.data,
          options: chartData.options
        })
        this.chart = chart
        return chart
      }
    },
    refreshChart () {
      this.findOrCreateChart(this.chartName, this.chartData)
      this.legendMarkup = this.chart.generateLegend()
    }
  },
  created: function () {
    this.chartName = _uuid()
    this.$nextTick(() => {
      this.refreshChart()
    })
  }
}
</script>

<style lang="css">
div.square {
  border: solid 5px;
  display: inline;
}

.noselect {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.doughnut-chart {
  position:relative;
  height: 20vh;
  width: 20vh;
  top:2%;
  margin:auto;
}
.chartjs-legend {
  width:100%;
  bottom:10%;
  top: 22%;
  text-transform: uppercase;
}
</style>
